import { auth } from './firebase';
import { startArtifactDownload } from '../utils/artifactDownload';
import { uploadDiligenceFile } from './diligenceUploadService';
import {
  getDiligenceUploadErrorMessage,
  getUnsupportedDiligenceFormatMessage
} from '../utils/diligenceUploadErrors';
import {
  createDiligenceUploadHttpFailure,
  DiligenceUploadFailure
} from './diligenceUploadFailure';

const ENDPOINT = '/api/diligence-attachments';
const MAX_BYTES = 20 * 1024 * 1024;
const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf', doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
};

export async function downloadChatDocument(chatId: string, messageId: string) {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para descargar el documento.');
  const token = await user.getIdToken();
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'downloadOriginal', chatId, messageId })
  });
  if (!response.ok) throw new Error('No se pudo preparar la descarga del documento. Inténtalo de nuevo.');
  const result = await response.json() as { downloadUrl: string };
  startArtifactDownload(result.downloadUrl);
}

export const registerDiligenceAttachment = async (chatId: string, file: File) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para adjuntar documentos.');
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  const unsupportedFormatMessage = getUnsupportedDiligenceFormatMessage(file.name);
  if (unsupportedFormatMessage) {
    throw new DiligenceUploadFailure(unsupportedFormatMessage, {
      stage: 'prepare', errorKind: 'unsupported_file_format'
    });
  }
  if (file.size > MAX_BYTES) {
    throw new DiligenceUploadFailure('El documento no puede superar 20 MiB.', {
      stage: 'prepare', errorKind: 'file_too_large'
    });
  }
  const contentType = MIME_BY_EXTENSION[extension];
  const token = await user.getIdToken();
  const prepareResponse = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'prepareUpload', chatId, originalFileName: file.name, contentType, size: file.size })
  });
  if (!prepareResponse.ok) {
    const failure = await createDiligenceUploadHttpFailure(
      prepareResponse, 'prepare', 'No se pudo preparar el documento adjunto.'
    );
    throw new DiligenceUploadFailure(
      getDiligenceUploadErrorMessage('attachment', failure.errorKind),
      { ...failure, stage: 'prepare', cause: failure }
    );
  }
  const prepared = await prepareResponse.json() as { attachmentId: string; uploadUrl: string; storagePath: string; contentType: string; originalFileName: string };
  await uploadDiligenceFile(prepared.uploadUrl, file, prepared.contentType);
  const finalizeResponse = await fetch(ENDPOINT, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'finalize', chatId, attachmentId: prepared.attachmentId, storagePath: prepared.storagePath, originalFileName: prepared.originalFileName, contentType: prepared.contentType, size: file.size })
  });
  if (!finalizeResponse.ok) {
    const failure = await createDiligenceUploadHttpFailure(
      finalizeResponse, 'finalize', 'No se pudo registrar el documento adjunto.'
    );
    throw new DiligenceUploadFailure(
      getDiligenceUploadErrorMessage('attachment', failure.errorKind),
      { ...failure, stage: 'finalize', cause: failure }
    );
  }
  return prepared.attachmentId;
};
